import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { ProductUnit } from '@/api/suppliers'
import { IconCheck, IconClose, IconPlus } from '@/components/icons'
import { PRESETS, STEP, addQuantity, formatQty } from '@/lib/quantity'

/**
 * Ποσότητες με πατήματα, όχι με πληκτρολόγηση.
 *
 * Τα βελάκια του `input type="number"` έχουν ύψος οκτώ pixel και βρίσκονται
 * κολλητά το ένα πάνω στο άλλο: σε κινητό, με βρεγμένα ή αλευρωμένα χέρια μέσα
 * σε κουζίνα, είναι πρακτικά άχρηστα. Εδώ όλα γίνονται με στόχους 44 pixel και
 * η πληκτρολόγηση μένει μόνο ως έξοδος διαφυγής για ασυνήθιστες ποσότητες.
 */

/**
 * Ο βασικός χειριστήριος έλεγχος μιας γραμμής.
 *
 * Όσο η ποσότητα είναι μηδέν εμφανίζεται ένα μόνο κουμπί «+», ώστε η γραμμή να
 * μη γεμίζει με χειριστήρια που δεν χρειάζονται ακόμη.
 */
export function QuantityStepper({
  value,
  unit,
  onChange,
  compact = false,
}: {
  value: string
  unit: ProductUnit
  onChange: (value: string) => void
  compact?: boolean
}) {
  const { t } = useTranslation()
  const [picking, setPicking] = useState(false)
  const active = Number(value) > 0
  const step = STEP[unit]

  if (!active) {
    return (
      <button
        onClick={() => onChange(String(step))}
        aria-label={t('quantity.add')}
        className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-gray-100 text-gray-500 transition-colors hover:bg-brand-50 hover:text-brand-700 active:bg-brand-100"
      >
        <IconPlus size={22} />
      </button>
    )
  }

  return (
    <div className="relative shrink-0">
      <div className="flex items-center gap-0.5 rounded-2xl bg-brand-700 p-0.5 text-white">
        <button
          onClick={() => onChange(addQuantity(value, -step))}
          aria-label={t('quantity.less')}
          className="flex size-11 items-center justify-center rounded-[14px] text-2xl font-light leading-none transition-colors hover:bg-white/15 active:bg-white/25"
        >
          −
        </button>

        {/* Το ίδιο το νούμερο ανοίγει τις έτοιμες ποσότητες: από «1» σε «5»
            χωρίς τέσσερα πατήματα στο συν. */}
        <button
          onClick={() => setPicking((open) => !open)}
          aria-label={t('quantity.choose')}
          className={
            'flex h-11 items-center justify-center rounded-[14px] px-1 text-lg font-bold tabular-nums transition-colors hover:bg-white/15 ' +
            (compact ? 'min-w-11' : 'min-w-14')
          }
        >
          {formatQty(value)}
          {!compact && (
            <span className="ml-1 text-[11px] font-semibold text-brand-100">
              {t(`units.${unit}`)}
            </span>
          )}
        </button>

        <button
          onClick={() => onChange(addQuantity(value, step))}
          aria-label={t('quantity.more')}
          className="flex size-11 items-center justify-center rounded-[14px] text-2xl font-light leading-none transition-colors hover:bg-white/15 active:bg-white/25"
        >
          +
        </button>
      </div>

      {picking && (
        <QuantityPicker
          value={value}
          unit={unit}
          onPick={(next) => {
            onChange(next)
            setPicking(false)
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  )
}

/** Έτοιμες ποσότητες με ένα πάτημα, συν πεδίο για τα υπόλοιπα. */
function QuantityPicker({
  value,
  unit,
  onPick,
  onClose,
}: {
  value: string
  unit: ProductUnit
  onPick: (value: string) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [custom, setCustom] = useState('')

  return (
    <>
      <button
        aria-hidden
        tabIndex={-1}
        onClick={onClose}
        className="fixed inset-0 z-20 cursor-default"
      />
      <div className="absolute right-0 top-full z-30 mt-1.5 w-64 rounded-2xl border border-gray-200 bg-white p-2.5 shadow-lift">
        <div className="mb-2 flex items-center justify-between px-1">
          <span className="text-xs font-bold uppercase tracking-widest text-gray-500">
            {t('quantity.choose')}
          </span>
          <button
            onClick={onClose}
            aria-label={t('common.cancel')}
            className="rounded-lg p-1 text-gray-400 hover:text-gray-600"
          >
            <IconClose size={16} />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-1.5">
          {PRESETS[unit].map((preset) => {
            const selected = Number(value) === preset
            return (
              <button
                key={preset}
                onClick={() => onPick(String(preset))}
                className={
                  'flex h-11 items-center justify-center rounded-xl text-base font-bold tabular-nums transition-colors ' +
                  (selected
                    ? 'bg-brand-700 text-white'
                    : 'bg-gray-100 text-gray-800 hover:bg-brand-50 hover:text-brand-800')
                }
              >
                {formatQty(preset)}
              </button>
            )
          })}
        </div>

        {/* Η έξοδος διαφυγής: 3,7 κιλά ψάρι δεν χωράει σε έτοιμο κουμπί. */}
        <form
          className="mt-2 flex gap-1.5"
          onSubmit={(event) => {
            event.preventDefault()
            const parsed = Number(custom.replace(',', '.'))
            if (Number.isFinite(parsed) && parsed > 0) onPick(String(parsed))
          }}
        >
          <input
            inputMode="decimal"
            value={custom}
            onChange={(event) => setCustom(event.target.value)}
            placeholder={t('quantity.other')}
            className="h-11 min-w-0 flex-1 rounded-xl border border-gray-300 bg-white px-3 text-base text-gray-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
          />
          <button
            type="submit"
            aria-label={t('common.save')}
            className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-brand-700 text-white hover:bg-brand-600"
          >
            <IconCheck size={18} />
          </button>
        </form>

        <button
          onClick={() => onPick('')}
          className="mt-2 h-10 w-full rounded-xl text-sm font-semibold text-gray-500 hover:bg-gray-50 hover:text-red-600"
        >
          {t('quantity.remove')}
        </button>
      </div>
    </>
  )
}

/**
 * Τα συνηθισμένα είδη ως chips.
 *
 * Οι περισσότερες παραγγελίες είναι οι ίδιες δέκα γραμμές κάθε φορά. Ένα
 * πάτημα εδώ γλιτώνει κύλιση σε ολόκληρη τη λίστα.
 */
export function FavoriteChips({
  items,
  quantities,
  onBump,
}: {
  items: { key: string; name: string; unit: ProductUnit; defaultQuantity: string }[]
  quantities: Record<string, string>
  onBump: (key: string, value: string) => void
}) {
  const { t } = useTranslation()
  if (items.length === 0) return null

  return (
    <div className="mb-3">
      <p className="mb-1.5 text-xs font-bold uppercase tracking-widest text-gray-500">
        {t('list.usual')}
      </p>
      {/* Οριζόντια κύλιση αντί για αναδίπλωση: τα chips μένουν σε μία γραμμή
          και δεν σπρώχνουν τη λίστα προς τα κάτω. */}
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
        {items.map((item) => {
          const current = quantities[item.key] ?? ''
          const active = Number(current) > 0

          return (
            <button
              key={item.key}
              onClick={() =>
                onBump(
                  item.key,
                  active
                    ? addQuantity(current, STEP[item.unit])
                    : String(Number(item.defaultQuantity) || STEP[item.unit]),
                )
              }
              className={
                'flex h-11 shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-semibold transition-colors ' +
                (active
                  ? 'border-brand-700 bg-brand-700 text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-brand-400 hover:bg-brand-50')
              }
            >
              {item.name}
              {active ? (
                <span className="rounded-full bg-white/20 px-1.5 text-xs tabular-nums">
                  {formatQty(current)}
                </span>
              ) : (
                <IconPlus size={15} className="opacity-60" />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
